import { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap, Tooltip, useMapEvents, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Import shared map utilities (DACSAI standard)
import {
  createStoreIcon,
  userLocationIcon,
  getZoomLevel,
  calculateDistanceMiles,
  calculatePointAtDistance,
  COLORS
} from '../../utils/mapUtils';

// Fix leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Marker types (defined in mapUtils.js):
// GREEN = DRLP (DealShaq Partner) inside DACSAI radius
// YELLOW = DRLP (DealShaq Partner) outside DACSAI radius
// RED = Non-DRLP store (regular grocery, not a DealShaq partner)

// Component to update map view when center or zoom changes
function MapUpdater({ center, radius, isFullscreen }) {
  const map = useMap();
  
  useEffect(() => {
    if (center && center.lat && center.lng && map) {
      try {
        map.setView([center.lat, center.lng], getZoomLevel(radius));
      } catch (e) {
        console.warn('MapUpdater setView error:', e);
      }
    }
  }, [center, radius, map]);
  
  // Invalidate size when fullscreen changes to ensure proper rendering
  useEffect(() => {
    if (!map) return;
    
    const timer = setTimeout(() => {
      try {
        if (map && map._container) {
          map.invalidateSize();
        }
      } catch (e) {
        console.warn('MapUpdater invalidateSize error:', e);
      }
    }, 200);
    
    return () => clearTimeout(timer);
  }, [isFullscreen, map]);
  
  return null;
}

// Component to handle map click for fullscreen toggle
function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click: (e) => {
      // Only trigger fullscreen if not clicking on a marker or popup
      if (e.originalEvent && 
          !e.originalEvent.target.closest('.leaflet-marker-icon') && 
          !e.originalEvent.target.closest('.leaflet-popup')) {
        onMapClick();
      }
    },
  });
  return null;
}

// Component to render radius line with arrow and label
function RadiusLine({ center, radiusMiles }) {
  const map = useMap();
  const [labelPosition, setLabelPosition] = useState(null);
  
  // Calculate endpoint at 90 degrees (East)
  const endPoint = calculatePointAtDistance(center, radiusMiles, 90);
  
  // Calculate midpoint for the label
  const midPoint = {
    lat: (center.lat + endPoint.lat) / 2,
    lng: (center.lng + endPoint.lng) / 2
  };
  
  // Update label position when map moves
  useEffect(() => {
    if (!map) return;
    
    const updatePosition = () => {
      const point = map.latLngToContainerPoint([midPoint.lat, midPoint.lng]);
      setLabelPosition(point);
    };
    
    updatePosition();
    map.on('move', updatePosition);
    map.on('zoom', updatePosition);
    
    return () => {
      map.off('move', updatePosition);
      map.off('zoom', updatePosition);
    };
  }, [map, midPoint.lat, midPoint.lng]);
  
  // Create arrowhead points
  const arrowLength = radiusMiles * 0.08; // Arrow length relative to radius
  const arrowPoint1 = calculatePointAtDistance(endPoint, arrowLength, 90 + 150); // 150 degrees back
  const arrowPoint2 = calculatePointAtDistance(endPoint, arrowLength, 90 - 150); // -150 degrees back
  
  return (
    <>
      {/* Main radius line */}
      <Polyline
        positions={[[center.lat, center.lng], [endPoint.lat, endPoint.lng]]}
        pathOptions={{
          color: '#059669',
          weight: 3,
          opacity: 0.9,
          dashArray: null
        }}
      />
      {/* Arrow head */}
      <Polyline
        positions={[
          [arrowPoint1.lat, arrowPoint1.lng],
          [endPoint.lat, endPoint.lng],
          [arrowPoint2.lat, arrowPoint2.lng]
        ]}
        pathOptions={{
          color: '#059669',
          weight: 3,
          opacity: 0.9,
          lineCap: 'round',
          lineJoin: 'round'
        }}
      />
      {/* Radius label - rendered as HTML overlay */}
      {labelPosition && (
        <div 
          style={{
            position: 'absolute',
            left: labelPosition.x,
            top: labelPosition.y - 25,
            transform: 'translateX(-50%)',
            zIndex: 1000,
            pointerEvents: 'none'
          }}
        >
          <div style={{
            backgroundColor: 'white',
            border: '2px solid #059669',
            borderRadius: '6px',
            padding: '2px 8px',
            fontSize: '12px',
            fontWeight: 'bold',
            color: '#059669',
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
          }}>
            {radiusMiles.toFixed(1)} mi
          </div>
        </div>
      )}
    </>
  );
}

// Get store type badge color
function getStoreTypeBadge(type) {
  const typeMap = {
    'Grocery': { bg: 'bg-emerald-100', text: 'text-emerald-700' },
    'Grocery Store': { bg: 'bg-emerald-100', text: 'text-emerald-700' },
    'Supermarket': { bg: 'bg-blue-100', text: 'text-blue-700' },
    'Specialty': { bg: 'bg-purple-100', text: 'text-purple-700' },
    'Convenience': { bg: 'bg-orange-100', text: 'text-orange-700' },
    'Organic': { bg: 'bg-green-100', text: 'text-green-700' },
    'Natural Foods': { bg: 'bg-lime-100', text: 'text-lime-700' },
  };
  return typeMap[type] || { bg: 'bg-gray-100', text: 'text-gray-700' };
}

export default function DacsaiMap({ 
  userLocation,
  userAddress,
  dacsaiRadius,
  onRadiusChange,  // Callback to update radius from fullscreen mode
  allStores = [],  // Array of all stores (both DRLP and non-DRLP)
  className = ''
}) {
  const mapRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [localRadius, setLocalRadius] = useState(dacsaiRadius);
  const [activeFilter, setActiveFilter] = useState(null); // null = show all, 'green', 'yellow', 'red'
  
  const defaultCenter = { lat: 40.7128, lng: -74.006 };
  const center = userLocation?.lat ? userLocation : defaultCenter;
  
  // Sync local radius with prop
  useEffect(() => {
    setLocalRadius(dacsaiRadius);
  }, [dacsaiRadius]);
  
  const currentRadius = isFullscreen ? localRadius : dacsaiRadius;
  const radiusMeters = currentRadius * 1609.34;
  
  // Handle radius change in fullscreen
  const handleRadiusChange = (value) => {
    setLocalRadius(value[0]);
    if (onRadiusChange) {
      onRadiusChange(value[0]);
    }
  };
  
  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isFullscreen]);
  
  // Prevent body scroll when fullscreen
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);
  
  const handleMapClick = () => {
    setIsFullscreen(true);
  };
  
  const handleExitFullscreen = (e) => {
    e.stopPropagation();
    setIsFullscreen(false);
  };
  
  // Categorize stores into three types
  const categorizedStores = allStores.map(store => {
    const coords = store.location;
    if (!coords || (coords.lat === 0 && coords.lng === 0)) return null;
    
    const distance = userLocation?.lat 
      ? calculateDistanceMiles(userLocation, coords)
      : Infinity;
    
    const isInsideRadius = distance <= currentRadius;
    const isDrlp = store.is_drlp === true;
    
    // Determine marker type
    let markerType;
    if (!isDrlp) {
      markerType = 'non-drlp';  // RED - Not a DealShaq partner
    } else if (isInsideRadius) {
      markerType = 'drlp-inside';  // GREEN - DRLP inside DACSAI
    } else {
      markerType = 'drlp-outside';  // YELLOW - DRLP outside DACSAI
    }
    
    return {
      ...store,
      coords,
      distance: distance.toFixed(2),
      isInsideRadius,
      isDrlp,
      markerType
    };
  }).filter(Boolean);
  
  // Sort: DRLP inside first, then DRLP outside, then non-DRLP, then by distance
  categorizedStores.sort((a, b) => {
    const order = { 'drlp-inside': 0, 'drlp-outside': 1, 'non-drlp': 2 };
    if (order[a.markerType] !== order[b.markerType]) {
      return order[a.markerType] - order[b.markerType];
    }
    return parseFloat(a.distance) - parseFloat(b.distance);
  });
  
  // Count each type
  const drlpInsideCount = categorizedStores.filter(s => s.markerType === 'drlp-inside').length;
  const drlpOutsideCount = categorizedStores.filter(s => s.markerType === 'drlp-outside').length;
  const nonDrlpCount = categorizedStores.filter(s => s.markerType === 'non-drlp').length;
  
  // Filter stores based on active filter
  const filteredStores = activeFilter === null 
    ? categorizedStores 
    : categorizedStores.filter(store => {
        if (activeFilter === 'green') {
          // Green: Show only DRLPs inside DACSAI (green markers only)
          return store.markerType === 'drlp-inside';
        } else if (activeFilter === 'yellow') {
          // Yellow: Show only DRLPs outside DACSAI (yellow markers only)
          return store.markerType === 'drlp-outside';
        } else if (activeFilter === 'red') {
          // Red: Show all non-DRLPs inside and outside
          return store.markerType === 'non-drlp';
        }
        return true;
      });
  
  // Handle legend button click
  const handleFilterClick = (filterType) => {
    if (activeFilter === filterType) {
      setActiveFilter(null); // Toggle off if same button clicked
    } else {
      setActiveFilter(filterType);
    }
  };
  
  // Reset filter (show all)
  const handleReset = () => {
    setActiveFilter(null);
  };
  
  if (!center.lat) {
    return (
      <div className={`rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center ${className}`}>
        <p className="text-gray-500 text-sm">Set your delivery address to see nearby stores</p>
      </div>
    );
  }
  
  // Fullscreen wrapper classes
  const fullscreenClasses = isFullscreen 
    ? 'fixed inset-0 z-[9999] bg-white' 
    : `relative ${className}`;
  
  const mapContainerClasses = isFullscreen
    ? 'h-full w-full'
    : 'h-full w-full';
  
  return (
    <div className={fullscreenClasses}>
      {/* Fullscreen header with close button and radius slider */}
      {isFullscreen && (
        <div className="absolute top-0 left-0 right-0 z-[10001] bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="8" cy="21" r="1"/>
                <circle cx="19" cy="21" r="1"/>
                <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/>
              </svg>
              <span className="font-semibold text-gray-800">Nearby Grocery Stores</span>
            </div>
            
            {/* Radius Slider in Fullscreen */}
            <div className="flex items-center gap-4 flex-1 max-w-md mx-8">
              <span className="text-sm text-gray-600 whitespace-nowrap">Shopping Radius:</span>
              <input
                type="range"
                min="0.1"
                max="9.9"
                step="0.1"
                value={localRadius}
                onChange={(e) => handleRadiusChange([parseFloat(e.target.value)])}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
              />
              <span className="text-sm font-bold text-emerald-600 whitespace-nowrap min-w-[60px]">{localRadius.toFixed(1)} mi</span>
            </div>
            
            <button 
              onClick={handleExitFullscreen}
              className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18"/>
                <path d="m6 6 12 12"/>
              </svg>
              Exit
            </button>
          </div>
        </div>
      )}
      
      {/* Click hint (only in non-fullscreen mode) */}
      {!isFullscreen && (
        <div className="absolute bottom-2 left-2 z-[1000] bg-black/60 text-white text-xs px-2 py-1 rounded pointer-events-none">
          Click map for fullscreen
        </div>
      )}
      
      {/* Map Legend - Clickable Filter Buttons */}
      <div className={`absolute ${isFullscreen ? 'top-16' : 'top-2'} right-2 z-[1000] bg-white rounded-lg shadow-lg p-3 text-xs border border-gray-200`}>
        <div className="font-semibold text-gray-800 mb-2 flex items-center gap-1">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="8" cy="21" r="1"/>
            <circle cx="19" cy="21" r="1"/>
            <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/>
          </svg>
          Store Legend
        </div>
        
        {/* Green Button - Partner Retailers in Shopping Radius */}
        <button
          onClick={() => handleFilterClick('green')}
          className={`flex items-center gap-2 mb-1.5 w-full px-2 py-1.5 rounded-md transition-all ${
            activeFilter === 'green' 
              ? 'bg-green-100 ring-2 ring-green-500 ring-offset-1' 
              : 'hover:bg-gray-100'
          }`}
        >
          <div className="w-4 h-4 rounded-full bg-green-500 border-2 border-green-600 shadow-sm"></div>
          <span className="text-gray-700">Partner Retailers in Shopping Radius ({drlpInsideCount})</span>
        </button>
        
        {/* Yellow Button - DRLP outside */}
        <button
          onClick={() => handleFilterClick('yellow')}
          className={`flex items-center gap-2 mb-1.5 w-full px-2 py-1.5 rounded-md transition-all ${
            activeFilter === 'yellow' 
              ? 'bg-yellow-100 ring-2 ring-yellow-500 ring-offset-1' 
              : 'hover:bg-gray-100'
          }`}
        >
          <div className="w-4 h-4 rounded-full bg-yellow-500 border-2 border-yellow-600 shadow-sm"></div>
          <span className="text-gray-700">DRLP outside ({drlpOutsideCount})</span>
        </button>
        
        {/* Red Button - Non-DRLP */}
        <button
          onClick={() => handleFilterClick('red')}
          className={`flex items-center gap-2 mb-1.5 w-full px-2 py-1.5 rounded-md transition-all ${
            activeFilter === 'red' 
              ? 'bg-red-100 ring-2 ring-red-500 ring-offset-1' 
              : 'hover:bg-gray-100'
          }`}
        >
          <div className="w-4 h-4 rounded-full bg-red-500 border-2 border-red-600 shadow-sm"></div>
          <span className="text-gray-700">Non-DRLP ({nonDrlpCount})</span>
        </button>
        
        {/* White/Reset Button */}
        <button
          onClick={handleReset}
          className={`flex items-center gap-2 w-full px-2 py-1.5 rounded-md transition-all ${
            activeFilter === null 
              ? 'bg-gray-100 ring-2 ring-gray-400 ring-offset-1' 
              : 'hover:bg-gray-100'
          }`}
        >
          <div className="w-4 h-4 rounded-full bg-white border-2 border-gray-400 shadow-sm"></div>
          <span className="text-gray-700">Reset</span>
        </button>
        
        {/* Active filter indicator */}
        {activeFilter && (
          <div className="mt-2 pt-2 border-t border-gray-200 text-[10px] text-gray-500 text-center">
            Filtered view active
          </div>
        )}
      </div>
      
      <MapContainer
        ref={mapRef}
        center={[center.lat, center.lng]}
        zoom={getZoomLevel(currentRadius)}
        className={isFullscreen ? '' : 'rounded-lg border border-gray-200'}
        style={{ 
          height: isFullscreen ? 'calc(100% - 56px)' : '100%', 
          width: '100%',
          marginTop: isFullscreen ? '56px' : '0'
        }}
        scrollWheelZoom={true}
        doubleClickZoom={true}
        zoomControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapUpdater center={center} radius={currentRadius} isFullscreen={isFullscreen} />
        <MapClickHandler onMapClick={handleMapClick} />
        
        {/* Shopping Radius Circle */}
        <Circle
          center={[center.lat, center.lng]}
          radius={radiusMeters}
          pathOptions={{
            color: '#059669',
            fillColor: '#10b981',
            fillOpacity: 0.12,
            weight: 3,
            dashArray: '10, 6'
          }}
        />
        
        {/* Radius line with arrow and label */}
        <RadiusLine center={center} radiusMiles={currentRadius} />
        
        {/* User's delivery location marker */}
        <Marker 
          position={[center.lat, center.lng]} 
          icon={userLocationIcon}
        >
          <Popup>
            <div className="min-w-[220px] max-w-[280px]">
              {/* Header */}
              <div className="flex items-start gap-2 mb-3 border-b border-gray-100 pb-2">
                <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="1">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22" fill="#3b82f6"/>
                  </svg>
                </div>
                <div>
                  <strong className="text-blue-700 text-sm block">Your Delivery Location</strong>
                  <span className="text-[10px] text-gray-500">Home Address</span>
                </div>
              </div>
              
              {/* Full Address */}
              {userAddress && (
                <div className="flex items-start gap-2 mb-2 text-xs">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  <span className="text-gray-700 font-medium">{userAddress}</span>
                </div>
              )}
              
              {/* Stats */}
              <div className="bg-blue-50 rounded-lg p-2.5 text-xs space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-blue-700">Shopping Radius:</span>
                  <span className="font-bold text-blue-800">{dacsaiRadius.toFixed(1)} miles</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-green-700">DRLP in radius:</span>
                  <span className="font-bold text-green-600">{drlpInsideCount} stores</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-yellow-700">DRLP outside:</span>
                  <span className="font-medium text-yellow-600">{drlpOutsideCount} stores</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-red-700">Non-DRLP:</span>
                  <span className="font-medium text-red-600">{nonDrlpCount} stores</span>
                </div>
              </div>
            </div>
          </Popup>
        </Marker>
        
        {/* Store Markers */}
        {filteredStores.map((store) => {
          const storeName = store.name || 'Grocery Store';
          const storeType = store.store_type || 'Grocery Store';
          const storeHours = store.hours || 'Hours not available';
          const storeAddress = store.address || 'Address not available';
          const storePhone = store.phone;
          const typeBadge = getStoreTypeBadge(storeType);
          
          // Determine status text and colors based on marker type
          let statusText, statusColor, statusBg;
          if (store.markerType === 'drlp-inside') {
            statusText = '✓ Partner Retailer - Inside your Shopping Radius';
            statusColor = 'text-green-700';
            statusBg = 'bg-green-500';
          } else if (store.markerType === 'drlp-outside') {
            statusText = '○ Partner Retailer - Outside your Shopping Radius';
            statusColor = 'text-yellow-700';
            statusBg = 'bg-yellow-500';
          } else {
            statusText = '✗ Not a DealShaq Partner';
            statusColor = 'text-red-700';
            statusBg = 'bg-red-500';
          }
          
          return (
            <Marker
              key={store.id || `${store.coords.lat}-${store.coords.lng}`}
              position={[store.coords.lat, store.coords.lng]}
              icon={createStoreIcon(store.markerType)}
            >
              {/* Tooltip showing store name on hover */}
              <Tooltip 
                direction="top" 
                offset={[0, -20]} 
                opacity={0.95}
                className="store-tooltip"
              >
                <div className="text-xs font-medium">{storeName}</div>
                <div className="text-[10px] text-gray-500">
                  {store.isDrlp ? 'DealShaq Partner' : 'Regular Store'}
                </div>
              </Tooltip>
              
              {/* Rich Popup with store details */}
              <Popup className="store-popup">
                <div className="min-w-[240px] max-w-[280px]">
                  {/* Store Header */}
                  <div className="border-b border-gray-100 pb-2 mb-2">
                    <div className="flex items-start gap-2">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        store.markerType === 'drlp-inside' ? 'bg-green-100' : 
                        store.markerType === 'drlp-outside' ? 'bg-yellow-100' : 'bg-red-100'
                      }`}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={
                          store.markerType === 'drlp-inside' ? '#16a34a' : 
                          store.markerType === 'drlp-outside' ? '#ca8a04' : '#dc2626'
                        } strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="8" cy="21" r="1"/>
                          <circle cx="19" cy="21" r="1"/>
                          <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/>
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 text-sm leading-tight">{storeName}</h3>
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium mt-1 ${typeBadge.bg} ${typeBadge.text}`}>
                          {storeType}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Store Details */}
                  <div className="space-y-2 text-xs">
                    {/* Address */}
                    <div className="flex items-start gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
                        <circle cx="12" cy="10" r="3"/>
                      </svg>
                      <span className="text-gray-600">{storeAddress}</span>
                    </div>
                    
                    {/* Hours */}
                    <div className="flex items-start gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                      </svg>
                      <span className="text-gray-600">{storeHours}</span>
                    </div>
                    
                    {/* Phone (if available) */}
                    {storePhone && (
                      <div className="flex items-start gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                        </svg>
                        <span className="text-gray-600">{storePhone}</span>
                      </div>
                    )}
                    
                    {/* Distance */}
                    <div className="flex items-start gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                        <path d="M18 8c0 4.5-6 9-6 9s-6-4.5-6-9a6 6 0 0 1 12 0"/>
                        <circle cx="12" cy="8" r="2"/>
                        <path d="M8.835 14H5a1 1 0 0 0-.9.7l-2 6c-.1.1-.1.2-.1.3 0 .6.4 1 1 1h18c.6 0 1-.4 1-1 0-.1 0-.2-.1-.3l-2-6a1 1 0 0 0-.9-.7h-3.835"/>
                      </svg>
                      <span className="text-gray-600 font-medium">{store.distance} miles away</span>
                    </div>
                  </div>
                  
                  {/* Status Badge */}
                  <div className={`mt-3 pt-2 border-t border-gray-100 flex items-center gap-2 ${statusColor}`}>
                    <div className={`w-2 h-2 rounded-full ${statusBg}`}></div>
                    <span className="text-xs font-semibold">{statusText}</span>
                  </div>
                  
                  {/* DealShaq Badge (only for DRLP stores) */}
                  {store.isDrlp && (
                    <div className="mt-2 bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg p-2 flex items-center gap-2">
                      <span className="text-[10px] font-medium text-emerald-700 inline-flex items-center gap-1">
                        <img src="/branded-flame.svg" width="12" height="12" alt="DealShaq" className="inline-block" />
                        DealShaq Partner Store
                      </span>
                    </div>
                  )}
                  
                  {/* Non-partner message */}
                  {!store.isDrlp && (
                    <div className="mt-2 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg p-2 flex items-center gap-2">
                      <span className="text-[10px] font-medium text-red-700">Not a DealShaq partner - No deals available</span>
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
      
      {/* Custom CSS for popup styling */}
      <style>{`
        .store-tooltip {
          background: white !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 8px !important;
          padding: 6px 10px !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
        }
        .store-tooltip::before {
          border-top-color: white !important;
        }
        .leaflet-popup-content-wrapper {
          border-radius: 12px !important;
          padding: 0 !important;
          box-shadow: 0 4px 20px rgba(0,0,0,0.2) !important;
        }
        .leaflet-popup-content {
          margin: 12px !important;
        }
        .leaflet-popup-tip {
          background: white !important;
        }
        /* Fix zoom controls positioning in fullscreen */
        .leaflet-control-zoom {
          margin-top: ${isFullscreen ? '70px' : '10px'} !important;
          margin-left: 10px !important;
          border: none !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2) !important;
        }
        .leaflet-control-zoom a {
          width: 36px !important;
          height: 36px !important;
          line-height: 36px !important;
          font-size: 18px !important;
          background: white !important;
          color: #374151 !important;
          border: 1px solid #e5e7eb !important;
        }
        .leaflet-control-zoom a:hover {
          background: #f3f4f6 !important;
        }
        .leaflet-control-zoom-in {
          border-radius: 8px 8px 0 0 !important;
        }
        .leaflet-control-zoom-out {
          border-radius: 0 0 8px 8px !important;
          border-top: none !important;
        }
        /* Fullscreen slider styling */
        input[type="range"] {
          -webkit-appearance: none;
          appearance: none;
          height: 8px;
          background: #e5e7eb;
          border-radius: 4px;
        }
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          background: #059669;
          border-radius: 50%;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        input[type="range"]::-moz-range-thumb {
          width: 20px;
          height: 20px;
          background: #059669;
          border-radius: 50%;
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
      `}</style>
    </div>
  );
}
